import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/api";
import "./AuditLogViewer.css";

interface AuditLogRow {
  id: number;
  username?: string;
  role?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  details?: unknown;
  created_at?: string;
  [key: string]: unknown;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function AuditLogViewer() {
  const [limit, setLimit] = useState(200);
  const [query, setQuery] = useState("");
  const [username, setUsername] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set("limit", String(limit));
      if (username.trim()) qs.set("username", username.trim());
      if (action.trim()) qs.set("action", action.trim());
      if (entityType.trim()) qs.set("entity_type", entityType.trim());
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const data = (await apiGet(`/api/audit/logs?${qs.toString()}`)) as AuditLogRow[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setRows([]);
      setError((e as Error)?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [limit, username, action, entityType, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.username,
        r.role,
        r.action,
        r.entity_type,
        r.entity_id,
        safeJson(r.details),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const exportCsv = () => {
    const cols = ["id", "created_at", "username", "role", "action", "entity_type", "entity_id", "details"];
    const esc = (v: unknown) => {
      const s = v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
      const t = String(s).replace(/"/g, '""');
      return `"${t}"`;
    };
    const lines = [
      cols.join(","),
      ...filtered.map((r) =>
        [
          r.id,
          r.created_at,
          r.username,
          r.role,
          r.action,
          r.entity_type,
          r.entity_id,
          safeJson(r.details),
        ].map(esc).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="audit-view">
      <div className="audit-toolbar">
        <div className="audit-title">
          <h3>Audit Logs</h3>
          <p>Admin-only. Use this for troubleshooting and accountability.</p>
        </div>
        <div className="audit-controls">
          <input
            className="audit-search"
            placeholder="Filter (user / action / entity)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <input
            className="audit-mini"
            placeholder="User"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className="audit-mini"
            placeholder="Action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
          <input
            className="audit-mini"
            placeholder="Entity"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          />
          <input
            className="audit-mini"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From (date)"
          />
          <input
            className="audit-mini"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="To (date)"
          />
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
          </select>
          <button type="button" className="btn-glass" onClick={exportCsv} disabled={filtered.length === 0}>
            Export CSV
          </button>
          <button type="button" className="btn-glass" onClick={() => void load()} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="audit-error">⚠️ {error}</div>}

      <div className="mod-table-wrapper audit-table-wrap">
        <table className="audit-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className={isOpen ? "open" : ""}>
                    <td className="mono">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td>
                      <strong>{r.username || "—"}</strong>
                      <div className="muted">{r.role || ""}</div>
                    </td>
                    <td>
                      <span className={`audit-action-badge ${r.action === "CREATE" ? "create" : r.action === "DELETE" ? "delete" : "default"}`}>
                          {r.action || "—"}
                      </span>
                    </td>
                    <td className="mono audit-entity">{r.entity_type ? `${r.entity_type}:${r.entity_id ?? ""}` : "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                      >
                        {isOpen ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="audit-details">
                      <td colSpan={5} className="audit-details-cell">
                        <div className="audit-details-panel">
                            <pre className="audit-details-pre">
                              {safeJson(r.details)}
                            </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="audit-empty">
                  {loading ? "Loading…" : "No logs found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
